import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';
import HolidaysListing from './HolidaysListing';
import HotelsListing from './HotelsListing';
import CabsListing from './CabsListing';
import YatraListing from './YatraListing';
import MedicalTourism from './MedicalTourism';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const CustomSeoUrlHandler = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [seoData, setSeoData] = useState(null);

    useEffect(() => {
        const resolveUrl = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const searchParams = new URLSearchParams(location.search);
                const isPreview = searchParams.get('preview') === 'true';
                const targetUrl = location.pathname;

                const response = await axios.get(`${API_BASE_URL}/web-pages/custom-seo-url/resolve`, {
                    params: {
                        url: targetUrl,
                        preview: isPreview ? 'true' : undefined
                    }
                });

                if (response.data.success && response.data.data) {
                    setSeoData(response.data.data);
                } else {
                    setNotFound(true);
                }
            } catch (error) {
                console.warn('[CustomSeoUrlHandler] Route not found or inactive custom SEO URL:', location.pathname);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        resolveUrl();
    }, [location.pathname, location.search]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading custom page...</span>
                </div>
            </div>
        );
    }

    if (notFound || !seoData) {
        return (
            <>
                <SEO title="Page Not Found | MangoLoops" robots="noindex, follow" />
                <Header />
                <div className="container text-center py-5 my-5">
                    <div className="py-4">
                        <i className="bi bi-exclamation-triangle text-warning display-1"></i>
                        <h1 className="mt-3 fw-bold">404 - Page Not Found</h1>
                        <p className="text-muted lead fs-16 max-w-600 mx-auto">
                            The page you are looking for does not exist, has been removed, or is currently inactive.
                        </p>
                        <div className="mt-4 d-flex justify-content-center gap-3">
                            <Link to="/" className="btn btn-primary px-4 py-2 fw-semibold">
                                <i className="bi bi-house me-1"></i> Return Home
                            </Link>
                            <Link to="/holidays" className="btn btn-outline-secondary px-4 py-2 fw-semibold">
                                Browse Holidays
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Render linked module component with SEO data & forced parameters
    const { module: moduleName } = seoData;

    switch (moduleName) {
        case 'holidays':
            return <HolidaysListing customSeoData={seoData} forcedParams={seoData.query_params} />;
        case 'hotels':
            return <HotelsListing customSeoData={seoData} forcedParams={seoData.query_params} />;
        case 'cabs':
            return <CabsListing customSeoData={seoData} forcedParams={seoData.query_params} />;
        case 'yatras':
            return <YatraListing customSeoData={seoData} forcedParams={seoData.query_params} />;
        case 'medical_tourism':
            return <MedicalTourism customSeoData={seoData} forcedParams={seoData.query_params} />;
        default:
            return <HolidaysListing customSeoData={seoData} forcedParams={seoData.query_params} />;
    }
};

export default CustomSeoUrlHandler;
